<script setup lang="ts">
import { XButton, XSkeletonLoader } from '@clicksign/excalibur';
import { useRouteQuery } from '@vueuse/router';
import { useToast } from 'vue-toastification';
import { useI18n } from '#i18n';

import { useCurrentSignatureRequestKey, useModals } from '~~core';
import { BrandButton } from '~~base-ui';

import { useFetchRequirement } from '~~/domains/requirement/repositories/requirement';
import { useRequirement } from '~~/domains/requirement/composables';
import { RequirementAdvanceButton } from '~~/domains/requirement/components';
import { saveRequirement } from '~~/domains/requirement/services';

import CertificateChooseSvg from '../../../../assets/svg/certificate-choose.svg?component';
import DigitalCertificateCloudProviders from '../../../../components/DigitalCertificateCloudProviders/DigitalCertificateCloudProviders.vue';
import DigitalCertificateLocalSelect from '../../../../components/DigitalCertificateLocalSelect/DigitalCertificateLocalSelect.vue';
import DigitalCertificateHowItWorksModal from '../../../../components/DigitalCertificateHowItWorksModal/DigitalCertificateHowItWorksModal.vue';
import { useSubSteps } from '../../../../composables/use-sub-steps';
import { useRequirementStep } from '../../../../composables/use-requirement-step';

const enum SubStep {
  Choose = 'choose',
  Local = 'local',
  Cloud = 'cloud',
}

const { setSubStep, isSubStep } = useSubSteps([
  SubStep.Choose,
  SubStep.Local,
  SubStep.Cloud,
]);

const modalName = {
  howItWorks: 'how-it-works',
};

const { closeModal, openModal, isModalOpen } = useModals();
const { finishRequirement, setActiveRequirement } = useRequirementStep();

const STEP_NAME = 'icp_brasil';

setActiveRequirement(STEP_NAME);

const { signatureRequestKey } = useCurrentSignatureRequestKey();
const { requirement, isLoading } = useFetchRequirement(STEP_NAME, signatureRequestKey);
const { isFilled: isReadonly } = useRequirement(requirement);

const { t } = useI18n();
const toast = useToast();

async function advanceStep(certificateKey: string) {
  if (!isReadonly.value) {
    try {
      await saveRequirement('icp_brasil', signatureRequestKey.value, { certificateKey });
    } catch (error) {
      toast.error(t('requirement.digitalCertificate.error.unexpected'));
      throw error;
    }
  }

  finishRequirement();
}

const gtm = useGtm();
const isReadyToComplete = useRouteQuery('ready_to_complete', undefined, { transform: (value: unknown) => value !== undefined });

function selectCertificateType(certificateType: SubStep) {
  setSubStep(certificateType);

  gtm?.trackEvent({
    event: 'ga-send',
    ga_event_category: 'sign-step-digital-certificate',
    ga_event_name: `button-${certificateType}-certificate`,
  });
}

onMounted(() => {
  if (isReadyToComplete.value) setSubStep(SubStep.Cloud);
});
</script>

<template>
  <div
    v-if="isLoading"
    :class="$style.loading"
  >
    <XSkeletonLoader
      height="15rem"
      width="224"
      rounded
    />

    <h3>
      <XSkeletonLoader
        height="2.8rem"
        rounded
      />
    </h3>

    <p>
      <XSkeletonLoader
        height="1em"
        rounded
      />
    </p>

    <div :class="$style.actions">
      <XSkeletonLoader
        height="4.8rem"
        rounded
      />

      <XSkeletonLoader
        height="4.8rem"
        rounded
      />
    </div>
  </div>

  <div
    v-else
    :class="$style.digitalCertificate"
  >
    <div
      v-show="isSubStep(SubStep.Choose)"
      :class="$style.types"
    >
      <CertificateChooseSvg width="224" />

      <h3>
        {{ $t('requirement.digitalCertificate.title') }}
      </h3>

      <p v-if="!isReadonly">
        {{ $t("requirement.digitalCertificate.description") }}
      </p>

      <div :class="$style.actions">
        <BrandButton
          v-if="!isReadonly"
          data-testid="digitalCertificateLocalButton"
          @click="selectCertificateType(SubStep.Local)"
        >
          {{ $t("requirement.digitalCertificate.localCertificate") }}
        </BrandButton>

        <BrandButton
          v-if="!isReadonly"
          data-testid="digitalCertificateCloudButton"
          @click="selectCertificateType(SubStep.Cloud)"
        >
          {{ $t("requirement.digitalCertificate.cloudCertificate") }}
        </BrandButton>

        <RequirementAdvanceButton
          v-if="!!isReadonly"
          full-width
          data-testid="requirementNext"
          @click="advanceStep"
        />
      </div>

      <XButton
        design="link"
        :class="$style.help"
        @click="openModal(modalName.howItWorks)"
      >
        {{ $t("requirement.digitalCertificate.help") }}
      </XButton>
    </div>

    <DigitalCertificateLocalSelect
      v-if="isSubStep(SubStep.Local)"
      @back="setSubStep(SubStep.Choose)"
      @completed="advanceStep"
    />

    <DigitalCertificateCloudProviders
      v-if="isSubStep(SubStep.Cloud)"
      :is-ready-to-complete="isReadyToComplete"
      @back="setSubStep(SubStep.Choose)"
      @completed="advanceStep"
    />

    <DigitalCertificateHowItWorksModal
      v-if="isModalOpen(modalName.howItWorks)"
      @close="closeModal()"
    />
  </div>
</template>

<style module lang="scss">
.digitalCertificate {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  max-width: 496px;
  margin: auto;
  text-align: center;
}

.loading {
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  max-width: 496px;
  margin: auto;
  margin-bottom: var(--space-regular);
  gap: var(--space-regular);
}

.types {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: var(--space-regular);
}

.actions {
  display: flex;
  justify-content: center;
  width: 100%;
  gap: var(--space-regular);
}

.help {
  margin-bottom: var(--space-regular);
  text-decoration: underline;
  color: var(--color-brand-primary-400);
}
</style>
